/**
 * The wire contract shared by every SDK and the collector.
 * Keep in sync with apps/collector-api/app/schemas.py.
 */

export type Direction = 'incoming' | 'outgoing';
export type Origin = 'frontend' | 'backend';

export type Cause =
  | 'user-action'
  | 'effect'
  | 'polling'
  | 'route-load'
  | 'cron'
  | 'webhook'
  | 'background-job'
  | 'incoming-request'
  | 'unknown';

export interface EventSource {
  file?: string | null;
  line?: number | null;
  /** Column of the call site — needed for accurate source-map resolution. */
  column?: number | null;
  function?: string | null;
  component?: string | null;
}

export interface EventError {
  type?: string | null;
  message?: string | null;
}

export interface RequestEvent {
  /** Client-generated UUID — idempotency key. */
  id: string;
  project_id: string;
  environment: string;
  direction: Direction;
  origin: Origin;
  method: string;
  url: string;
  host: string;
  path: string;
  status: number | null;
  ok: boolean | null;
  duration_ms: number | null;
  request_body_size: number | null;
  response_body_size: number | null;
  request_headers: Record<string, string> | null;
  response_headers: Record<string, string> | null;
  body_preview: string | null;
  trace_id: string | null;
  span_id: string | null;
  parent_span_id: string | null;
  source: EventSource | null;
  cause: Cause;
  planned: boolean;
  domain_known: boolean;
  user_id: string | null;
  session_id: string | null;
  error: EventError | null;
  tags: Record<string, unknown> | null;
  /** ISO 8601 */
  timestamp: string;
}

export interface IngestBatch {
  sdk: { name: string; version: string };
  events: RequestEvent[];
}

export interface Verdict {
  event_id: string;
  action: 'allow' | 'block';
  rule: string;
}

export interface IngestResponse {
  accepted: number;
  rejected: number;
  incidents_created: number;
  verdicts: Verdict[];
}

/** Trace propagation header names (W3C-traceparent compatible alongside these). */
export const TRACE_HEADERS = {
  traceId: 'x-aris-trace-id',
  spanId: 'x-aris-span-id',
  parentSpanId: 'x-aris-parent-span-id',
  origin: 'x-aris-origin',
  cause: 'x-aris-cause',
} as const;
